import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import type { ReactNode } from "react";

interface ActivityResponderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  icon: ReactNode;
  title: string;
  subtitle: string;
  contentClassName: string;
  bodyClassName?: string;
  canSend: boolean;
  isSending: boolean;
  onSend: () => void;
  children: ReactNode;
}

export function ActivityResponderDialog({
  isOpen,
  onClose,
  icon,
  title,
  subtitle,
  contentClassName,
  bodyClassName,
  canSend,
  isSending,
  onSend,
  children,
}: ActivityResponderDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          "flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-2xl p-0",
          contentClassName,
        )}
        aria-describedby={undefined}
      >
        <div
          className="flex flex-col gap-2 border-b px-8 pb-6 pt-8"
          style={{ borderColor: colorsV2.surfaces.divider }}
        >
          <div className="flex items-center gap-2">
            {icon}
            <DialogTitle
              className="text-2xl font-bold tracking-tight"
              style={{ color: colorsV2.text.primary }}
            >
              {title}
            </DialogTitle>
          </div>
          <p className="text-sm" style={{ color: colorsV2.text.secondary }}>
            {subtitle}
          </p>
        </div>

        <div className={cn("flex-1 overflow-y-auto px-8 py-6", bodyClassName)}>
          {children}
        </div>

        <div
          className="border-t px-8 pb-6 pt-6"
          style={{ borderColor: colorsV2.surfaces.divider }}
        >
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend || isSending}
            className={cn(
              "w-full rounded-xl py-3 text-base font-bold text-white",
              "focus:outline-none focus:ring-2 focus:ring-states-focused",
              canSend
                ? "bg-text-primary hover:bg-brand-trueBlack"
                : "cursor-not-allowed bg-states-inactive",
            )}
          >
            {isSending ? "Sending…" : "Send"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
